import { useCallback, useMemo, useState } from 'react';

import { Menu, MenuItem } from '@mui/material';
import { usePostHog } from 'posthog-js/react';

import { useChartContext } from '@/components/Chart/context/ChartContext';
import ExportButton from '@/components/Chart/ExportButton';
import type {
  ChartDownloadProperties,
  ChartParentComponent
} from '@/components/Chart/hooks/options/useChartFunctions';
import { useAppCsvExport } from '@/components/Chart/hooks/useAppCsvExport';
import useChartImageExport from '@/components/Chart/hooks/useChartImageExport';
import { usePageInformationContext } from '@/context/PageInformationContext';
import useUserDetails from '@/hooks/useUserDetails';

import type { ExportingMimeTypeValue } from 'highcharts';

const defaultProperties: ChartDownloadProperties = {
  format: '',
  fullScreen: false,
  darkMode: true,
  chartLocation: null
};

const CHART_EXPORT_OPTIONS: {
  format: string;
  mimeType: ExportingMimeTypeValue;
  label: string;
}[] = [
  { format: 'PNG', mimeType: 'image/png', label: 'Download PNG image' },
  { format: 'JPEG', mimeType: 'image/jpeg', label: 'Download JPEG image' },
  { format: 'PDF', mimeType: 'application/pdf', label: 'Download PDF document' },
  { format: 'SVG', mimeType: 'image/svg+xml', label: 'Download SVG vector image' }
];

const menuItemSx = { fontSize: 12, fontWeight: 600 };

const CustomDownloadChartButton = ({
  tabName,
  disabled = false
}: {
  tabName: string;
  disabled?: boolean;
}) => {
  const { isUserExpired } = useUserDetails();
  const isDisabled = disabled || isUserExpired;
  const { handleExport: handleCsvExport, exportDisabled } = useAppCsvExport(tabName, isDisabled);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { chartSeries } = useChartContext();
  const { onExport } = useChartImageExport();
  const postHog = usePostHog();

  // @ts-expect-error can be html
  const handleClick = useCallback((event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const { mainPageTitle, subPageTitle } = usePageInformationContext();
  const chartParent: ChartParentComponent = useMemo(
    () => ({
      mainPageTitle,
      subPageTitle,
      tabName
    }),
    [mainPageTitle, subPageTitle, tabName]
  );
  const captureChartDownloadAction = useCallback(
    (format: string) => {
      const properties = {
        format,
        fullScreen: true,
        darkMode: false,
        chartLocation: chartParent
      };
      const chartSymbols = chartSeries?.map(s => {
        const metadata = s.custom;
        return {
          Code: metadata.Code,
          Period: metadata.Period,
          PeriodType: metadata.PeriodType,
          TimeRef: metadata.TimeRef
        };
      });

      const mergedProperties: ChartDownloadProperties = { ...defaultProperties, ...properties };

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- postHog can be null when not configured
      postHog?.capture('chart_download_button_click', {
        symbols: chartSymbols,
        ...mergedProperties
      });
    },
    [chartParent, chartSeries, postHog]
  );

  const handleChartExport = useCallback(
    (format: string, mimeType: ExportingMimeTypeValue) => {
      captureChartDownloadAction(format);
      onExport(mimeType);
      handleClose();
    },
    [captureChartDownloadAction, onExport, handleClose]
  );

  return (
    <>
      <ExportButton
        disabled={isDisabled}
        handleClick={handleClick}
        title='Export chart'
      />
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        id='chart-export-menu'
        open={open}
        sx={{ zIndex: 9999999 }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        onClose={handleClose}
      >
        {CHART_EXPORT_OPTIONS.map(({ format, mimeType, label }) => (
          <MenuItem
            key={format}
            sx={menuItemSx}
            onClick={() => {
              handleChartExport(format, mimeType);
            }}
          >
            {label}
          </MenuItem>
        ))}
        {!exportDisabled && (
          <MenuItem
            sx={menuItemSx}
            onClick={handleCsvExport}
          >
            Download as CSV
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

export default CustomDownloadChartButton;
